import React, { useState, useEffect, useRef } from 'react';
import MapComponent from './components/MapComponent';
import LeafletMapComponent from './components/LeafletMapComponent';
import ActionButton from './components/ActionButton';
import './App.css';
import axios from './axiosConfig';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestion, faChevronRight, faSliders, faTimes } from '@fortawesome/free-solid-svg-icons';
import { Tooltip } from 'react-tooltip';
import ParametersPanel from './components/ParametersPanel';
import QuizPanel from './components/QuizPanel';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from './firebase';
import AlertMessage from './components/AlertMessage';
import ConfirmDialog from './components/ConfirmDialog';
import InputDialog from './components/InputDialog';

function App() {
  const [showCities, setShowCities] = useState(true);
  const [showStreets, setShowStreets] = useState(true);
  const [showPOI, setShowPOI] = useState(true);
  const [cities, setCities] = useState([]);
  const [createQuizMode, setCreateQuizMode] = useState(false);
  const [quizName, setQuizName] = useState('');
  const [points, setPoints] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [filteredQuizzes, setFilteredQuizzes] = useState([]); // New state for filtered quizzes
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [randomPoint, setRandomPoint] = useState(null);
  const [foundPoints, setFoundPoints] = useState([]);
  const [markers, setMarkers] = useState([]);
  const [skippedPoints, setSkippedPoints] = useState([]);
  const [skippedCounter, setSkippedCounter] = useState(0);
  const [displaySkippedPoints, setDisplaySkippedPoints] = useState([]);
  const [reviewPoints, setReviewPoints] = useState([]);
  const [showParameters, setShowParameters] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizInProgress, setQuizInProgress] = useState(false);
  const [editQuiz, setEditQuiz] = useState(null);
  const [isHintDisplayed, setIsHintDisplayed] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [tempQuizName, setTempQuizName] = useState('');
  const [localSearchTerm, setLocalSearchTerm] = useState(''); // New state for local search term
  const [globalSearchTerm, setGlobalSearchTerm] = useState(''); // New state for global search term
  const [isLoading, setIsLoading] = useState(true); // Add isLoading state
  const quizNameInputRef = useRef(null);
  const [alert, setAlert] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const quizPanelRef = useRef(null);
  const [inputDialog, setInputDialog] = useState(null);

  const [user] = useAuthState(auth);

  useEffect(() => {
    if (user) {
      fetchQuizzes();
    }
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (quizPanelRef.current && !quizPanelRef.current.contains(event.target)) {
        setShowQuiz(false);
      }
    };

    // Écouteur d'événements pour les clics en dehors du QuizPanel
    if (showQuiz) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showQuiz]);

  const showAlert = (type, message) => {
    setAlert({ type, message });
  };

  const fetchQuizzes = async () => {
    setIsLoading(true); // Set loading to true before fetching
    try {
	await axios.post('/api/auth/check-register-uid', {
		uid: user.uid,
		email: user.email,
	  }, {
		withCredentials: true, // Assure-toi que cette option est bien activée
	  });
      const response = await axios.get('/api/quizzes', {
        params: {
          uid: user.uid,
        },
      });
      const quizzesData = response.data;
      setQuizzes(quizzesData);
      setFilteredQuizzes(quizzesData); // Initialize filtered quizzes
    } catch (error) {
      showAlert('error', 'Erreur lors de la récupération des quiz.');
    } finally {
      setIsLoading(false); // Set loading to false after fetching
    }
  };

  const toggleShowCities = () => {
    setShowCities(prevShowCities => !prevShowCities);
  };

  const toggleShowStreets = () => {
    setShowStreets(prevShowStreets => !prevShowStreets);
  };

  const toggleShowPOI = () => {
    setShowPOI(prevShowPOI => !prevShowPOI);
  };

  const handleAddCity = (city) => {
    setCities([...cities, city]);
  };

  const handleCreateQuiz = () => {
	setInputDialog({
	  message: '',
	  placeholder: 'Entrez le nom ici...',
	  onConfirm: (name) => {
		setCreateQuizMode(true);
		setShowQuiz(false);
		setQuizName(name);
		setTempQuizName(name);
		setEditQuiz(null); // Reset edit mode
		setInputDialog(null); // Ferme l'InputDialog
	  },
	  onCancel: () => setInputDialog(null) // Ferme l'InputDialog
	});
  };

  // Fonction appelée lorsque l'utilisateur valide le nom du quiz
  const handleConfirm = (name) => {
    setQuizName(name); // Définit le nom du quiz
	setTempQuizName(name);
    setCreateQuizMode(true); // Active le mode création de quiz
    setInputDialog(false); // Ferme l'InputDialog
	setShowQuiz(false);
  };

  // Fonction appelée lorsque l'utilisateur annule l'entrée du nom du quiz
  const handleCancel = () => {
    setInputDialog(false); // Ferme l'InputDialog sans aucune action
  };

  const handleValidateQuiz = async () => {
    if (points.length === 0) {
      showAlert('error', 'Ajoutez un ou des points sur la carte avant de valider.');
      return;
    }
    setIsLoading(true); // Set loading to true
    try {
      if (editQuiz) {
        // Update existing quiz
        await axios.put(`/api/quizzes/${editQuiz.id}`, {
          name: tempQuizName, // Use tempQuizName for updating
          points: points
        });
      } else {
        // Create new quiz
        const response = await axios.post('/api/quizzes', {
          name: tempQuizName, // Use tempQuizName for creation
          points: points,
          uid: user.uid, // Include the UID of the logged-in user
		  email: user.email
        });
        setQuizzes([...quizzes, response.data]);
        setFilteredQuizzes([...quizzes, response.data]);
      }
      fetchQuizzes();
      setPoints([]);
      setCreateQuizMode(false);
      setShowQuiz(true);
    } catch (error) {
      showAlert('error', 'Erreur lors de la sauvegarde des quiz');
    } finally {
      setIsLoading(false); // Set loading to false
    }
  };

  const handleCancelQuiz = () => {
    setConfirmDialog({
      message: 'Attention, vos modifications vont être perdues. Voulez-vous vraiment annuler ?',
      onConfirm: () => {
        setCreateQuizMode(false);
        setPoints([]);
        setQuizName(editQuiz ? editQuiz.name : '');
        setTempQuizName(editQuiz ? editQuiz.name : '');
        setEditQuiz(null);
        setShowQuiz(true);
        setConfirmDialog(null);
      },
      onCancel: () => setConfirmDialog(null)
    });
  };

  const handleRemovePoint = (point) => {
    setPoints(points.filter(p => p.lat !== point.lat || p.lng !== point.lng));
  };

  const handleAddPoint = (point) => {
    setPoints([...points, point]);
  };

  const handleQuizClick = async (quizId) => {
    try {
      const response = await axios.get(`/api/quizzes/${quizId}`);
      const quiz = response.data;
      setSelectedQuiz(quiz);
      setFoundPoints([]);
      setMarkers([]);
      setSkippedPoints([]);
      setSkippedCounter(0);
      setQuizInProgress(true);  // Set quiz in progress
      setIsHintDisplayed(false); // Reset hint display
      selectRandomPoint(quiz, []);
    } catch (error) {
      showAlert('error', 'Erreur lors de la récupération des quiz.');
    }
  };

  const handleEditQuiz = async (quizId) => {
    try {
      const response = await axios.get(`/api/quizzes/${quizId}`);
      const quiz = response.data;
      setEditQuiz(quiz);
      setCreateQuizMode(true);
      setPoints(quiz.points);
      setQuizName(quiz.name);
      setTempQuizName(quiz.name);
      setShowQuiz(false);
    } catch (error) {
      showAlert('error', 'Erreur lors de la récupération des quiz.');
    }
  };

  const handleDeleteQuiz = async (quizId) => {
    setConfirmDialog({
      message: `Attention, vous allez supprimer le quiz. Voulez-vous vraiment le supprimer ?`,
      onConfirm: async () => {
        setIsLoading(true);
        try {
          await axios.delete(`/api/quizzes/${quizId}`, {
            params: {
              uid: user.uid
            }
          });
          const updatedQuizzes = quizzes.filter(quiz => quiz.id !== quizId);
          setQuizzes(updatedQuizzes);
          setFilteredQuizzes(updatedQuizzes);
          showAlert('success', 'Le quiz a été supprimé avec succès.');
        } catch (error) {
          showAlert('error', "Le quiz n'a pas pu être supprimé.");
        } finally {
          setIsLoading(false);
        }
        setConfirmDialog(null);
      },
      onCancel: () => setConfirmDialog(null)
    });
  };

  const handlePlayQuiz = (quizId) => {
    handleQuizClick(quizId);
    setShowQuiz(false);
  };

  const handleImportQuiz = async () => {
	const postData = {
	  code: globalSearchTerm.trim(), // Ensure code is a trimmed string
	  uid: user.uid.trim() // Ensure uid is a trimmed string
	};
  
	try {
	  const response = await axios.post('/api/quizzes/import', postData);
	  if (response.data.success) {
      showAlert('success', 'Le quiz a été importé avec succès.');
      setGlobalSearchTerm(''); // Reset the global search term
      fetchQuizzes(); // Fetch quizzes after successful import
	  } else if (response.data.message) {
      showAlert('error', 'Le quiz semble déjà importé.');
	  }
	} catch (error) {
	  showAlert('error', "Erreur lors de l'importation du quiz.");
	}
  };  

  const handleLocalSearch = (searchTerm) => {
    if (searchTerm === '') {
      setFilteredQuizzes(quizzes); // Show all quizzes if search term is empty
    } else {
      const filtered = quizzes.filter(quiz =>
        quiz.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredQuizzes(filtered);
    }
    setLocalSearchTerm(searchTerm); // Update local search term
  };

  const handleGlobalSearch = (searchTerm) => {
    setGlobalSearchTerm(searchTerm); // Update global search term
  };

  const selectRandomPoint = (quiz, foundPoints, skippedPoints = [], reviewPoints = []) => {
    let remainingPoints = quiz.points.filter(point =>
      !foundPoints.some(foundPoint =>
        foundPoint.lat === point.lat && foundPoint.lng === point.lng
      ) &&
      !skippedPoints.some(skippedPoint =>
        skippedPoint.lat === point.lat && skippedPoint.lng === point.lng
      ) &&
      !reviewPoints.some(reviewPoint =>
        reviewPoint.lat === point.lat && reviewPoint.lng === point.lng
      )
    );

    // Créer une liste temporaire des points restants en excluant le point actuellement affiché
    const tempPoints = remainingPoints.filter(point =>
      !randomPoint || (point.lat !== randomPoint.lat || point.lng !== randomPoint.lng)
    );

    if (tempPoints.length > 0) {
      const randomPointIndex = Math.floor(Math.random() * tempPoints.length);
      const newRandomPoint = tempPoints[randomPointIndex];

      // Mettre à jour la liste des points restants en excluant le nouveau point sélectionné
      tempPoints.splice(randomPointIndex, 1);

      setRandomPoint(newRandomPoint);
    } else if (reviewPoints.length > 0) {
      const reviewPoint = reviewPoints[0];
      setRandomPoint(reviewPoint);
      setReviewPoints(reviewPoints.slice(1));
    } else if (skippedPoints.length > 0) {
      setRandomPoint(skippedPoints[0]);
      setSkippedPoints(skippedPoints.slice(1));
    } else {
      setRandomPoint(null);
      setMarkers([]);
      showAlert('success', 'Quiz terminé !');
      setQuizInProgress(false);
    }
  };

  const handleCorrectAnswer = () => {
    if (selectedQuiz) {
      setFoundPoints([...foundPoints, randomPoint]);
      setMarkers([...markers, randomPoint]);
      setIsHintDisplayed(false); // Allow correct answers to be processed
      if (reviewPoints.length < 4) {
        selectRandomPoint(selectedQuiz, [...foundPoints, randomPoint], skippedPoints, reviewPoints);
      } else {
        setReviewPoints([...reviewPoints, randomPoint]);
        selectRandomPoint(selectedQuiz, [...foundPoints, randomPoint], skippedPoints);
      }
    }
  };

  const handleSkipPoint = () => {
    if (selectedQuiz) {
      setDisplaySkippedPoints([...displaySkippedPoints, randomPoint]);
      setIsHintDisplayed(true); // Show hint and prevent correct answers
      if (reviewPoints.length < 4) {
        setSkippedPoints([...skippedPoints, randomPoint]);
      } else {
        setReviewPoints([...reviewPoints, randomPoint]);
      }
    }
  };

  const handleUnderstand = () => {
    if (randomPoint) {
      setSkippedCounter(skippedCounter + 1);
      setSkippedPoints(skippedPoints.filter(point => point.lat !== randomPoint.lat || point.lng !== randomPoint.lng));
      setDisplaySkippedPoints([]);
      setIsHintDisplayed(false); // Allow point addition after understanding
      selectRandomPoint(selectedQuiz, foundPoints, skippedPoints.filter(point => point.lat !== randomPoint.lat || point.lng !== point.lng), reviewPoints);
    }
  };

  const toggleParametersPanel = () => {
    setShowParameters(!showParameters);
    setShowQuiz(false);
  };

  const handleCloseParameters = () => {
    setShowParameters(false);
  };

  const toggleQuizPanel = () => {
    if (quizInProgress) {
      showAlert('error', "Vous ne pouvez pas accéder au menu sans avoir quitter le quiz en cours d'execution.");
      return;
    }
    setShowQuiz(!showQuiz);
    if (!showQuiz) {
      setLocalSearchTerm(''); // Reset local search term
      setFilteredQuizzes(quizzes); // Reset filtered quizzes
    }
    setShowParameters(false);
  };

  const handleCloseQuiz = () => {
    setShowQuiz(false);
    setLocalSearchTerm(''); // Reset local search term
    setFilteredQuizzes(quizzes); // Reset filtered quizzes
  };

  const handleStopQuiz = () => {
    setSelectedQuiz(null);
    setRandomPoint(null);
    setFoundPoints([]);
    setMarkers([]);
    setSkippedPoints([]);
    setSkippedCounter(0);
    setDisplaySkippedPoints([]);
    setReviewPoints([]);
    setQuizInProgress(false);
    setIsHintDisplayed(false); // Allow point addition after stopping quiz
  };

  const handleEditNameClick = () => {
    setIsEditingName(true);
  };

  const handleNameChange = (e) => {
    setTempQuizName(e.target.value);
  };

  return (
    <div className="App">
      <header>
        {(randomPoint || createQuizMode) && (
          <div className="header-container">
            {createQuizMode ? (
              isEditingName ? (
                <div className="edit-name-container" ref={quizNameInputRef}>
                  <input
                    type="text"
                    value={tempQuizName}
                    onChange={handleNameChange}
                  />
                </div>
              ) : (
                <h2 onClick={handleEditNameClick} data-tooltip-id="edit-name-tooltip" data-tooltip-content="Cliquez pour modifier le nom">
                  {tempQuizName}
                </h2>
              )
            ) : (
              <>
                <button onClick={handleSkipPoint}>
                  <FontAwesomeIcon icon={faQuestion} />
                </button>
                <h2>{randomPoint.name}</h2>
                <button onClick={handleUnderstand}>
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              </>
            )}
            <Tooltip id="tooltip" className="custom-tooltip" place="bottom" type="dark" effect="solid" />
            <Tooltip id="edit-name-tooltip" className="custom-tooltip" place="bottom" type="dark" effect="solid" />
          </div>
        )}
      </header>
      <div className="map-container">
        {createQuizMode ? (
          <LeafletMapComponent onAddPoint={handleAddPoint} onRemovePoint={handleRemovePoint} points={points} />
        ) : (
          <MapComponent
            showCities={showCities}
            showStreets={showStreets}
            showPOI={showPOI}
            onAddCity={handleAddCity}
            randomPoint={randomPoint}
            onCorrectAnswer={handleCorrectAnswer}
            markers={markers}
            displaySkippedPoints={displaySkippedPoints}
            isHintDisplayed={isHintDisplayed} // Pass isHintDisplayed to MapComponent
          />
        )}
        {createQuizMode && (
          <div className="validate-cancel-buttons">
            <ActionButton text="Valider" onClick={handleValidateQuiz} bgColor="#4CAF50" textColor="white" hoverBgColor="#45a049" hoverTextColor="white" />
            <ActionButton text="Annuler" onClick={handleCancelQuiz} bgColor="#f44336" textColor="white" hoverBgColor="#da190b" hoverTextColor="white" />
          </div>
        )}
      </div>
      {!createQuizMode && (
			<footer className="footer-container">
				<button
					className="footer-button"
					onClick={toggleParametersPanel}
					data-tooltip-id="tooltip"
					data-tooltip-content="Paramètres"
				>
					<FontAwesomeIcon icon={faSliders} style={{ color: '#024273', fontSize: '1.2em' }} />
				</button>
				<button className="footer-button" onClick={toggleQuizPanel}>
					Quiz
				</button>
				<button
					className={`footer-button ${quizInProgress && !createQuizMode ? '' : 'hidden'}`}
					onClick={handleStopQuiz}
					data-tooltip-id="tooltip"
					data-tooltip-content="Arrêter le quiz"
				>
					<FontAwesomeIcon icon={faTimes} style={{ color: '#ff0000', fontSize: '1.2em' }} />
				</button>
				<Tooltip id="tooltip" className="custom-tooltip" place="bottom" type="dark" effect="solid" />
			</footer>
		)}
      {showParameters && (
        <ParametersPanel
          onClose={handleCloseParameters}
          showCities={showCities}
          showStreets={showStreets}
          showPOI={showPOI}
          onToggleShowCities={toggleShowCities}
          onToggleShowStreets={toggleShowStreets}
          onToggleShowPOI={toggleShowPOI}
        />
      )}
      {alert && (
        <AlertMessage
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}
            {showQuiz && (
        <QuizPanel
          ref={quizPanelRef} // Associe le ref au QuizPanel
          quizzes={filteredQuizzes}
          isLoading={isLoading} 
          onClose={handleCloseQuiz}
          onQuizClick={handleQuizClick}
          onCreateQuiz={handleCreateQuiz}
          onEditQuiz={handleEditQuiz}
          onDeleteQuiz={handleDeleteQuiz}
          onPlayQuiz={handlePlayQuiz}
          onImportQuiz={handleImportQuiz}
          onLocalSearch={handleLocalSearch}
          onGlobalSearch={handleGlobalSearch}
        />
      )}

      {inputDialog && (
        <InputDialog
          message=""
          placeholder="Entrez le nom du quiz."
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      )}

      {confirmDialog && (
        <ConfirmDialog
          message={confirmDialog.message}
          onConfirm={confirmDialog.onConfirm}
          onCancel={confirmDialog.onCancel}
        />
      )}
    </div>
  );
}

export default App;
